import { WarningDiamondIcon } from "@xola/icons";
import { Alert, flash, FormGroup, Label, Modal, SubmitButton } from "@xola/ui-kit";
import { handleSuccessfulSubmission, requiredString } from "common";
import { FormikProps } from "formik";
import React, { ReactNode, useRef, useState } from "react";
import { useMutation } from "react-query";
import { CommunicationChannelType, MessageTemplate, Permission, SelectionState } from "types";
import { object, string } from "yup";
import { api } from "../../../api/api";
import { useSeller, useUser } from "../../../hooks/useAccount";
import { useCollection } from "../../../hooks/useCollection";
import { SecondaryButton } from "../SecondaryButton";
import { XolaFormik } from "../XolaFormik";
import { ChannelSwitch, MessageForm } from "./MessageForm";
import { FormState } from "./MessageModalTypes";
import { TemplateSelect } from "./TemplateSelect";
import { TravelerMessage, TravelersSelector } from "./TravelersSelector";
import { IsGranted } from "@/components/Common/IsGranted";

interface MessageSendTo {
    id?: ID;
    type: "traveler" | "travelers" | "guides" | "organizer";
}
export interface MessageModalProps {
    isOpen: boolean;
    isEmailAvailable?: boolean;
    isPhoneAvailable?: boolean;
    description?: string;
    filters: any;
    title?: string;
    to?: MessageSendTo[];
    travelersList?: TravelerMessage[];
    /** Content we want to show before the main form  */
    children?: ReactNode;
    /** Whether to show the "Email Guides" option. Defaults to true. Should be false for merchandise purchases. */
    shouldShowEmailGuides?: boolean;
    onClose: (e?: any) => void;
    /** (Optional) Callback fired after POST `messages` API responds with success status */
    onContinue?: (e?: any) => void;
}

export const MessageModal = ({ isOpen, onClose, description, title, ...rest }: MessageModalProps) => {
    return (
        <Modal isOpen={isOpen} className="overflow-visible" onClose={onClose}>
            <Modal.Header description={description}>{title ?? "Send Group Message"}</Modal.Header>

            <MessageModalContent onClose={onClose} {...rest} />
        </Modal>
    );
};

export type MessageModalContentProps = Omit<MessageModalProps, "isOpen" | "description">;

export const MessageModalContent = ({
    filters,
    to,
    children,
    onClose,
    onContinue,
    travelersList,
    isEmailAvailable = true,
    isPhoneAvailable = true,
    shouldShowEmailGuides = true,
}: MessageModalContentProps) => {
    const seller = useSeller();
    const user = useUser();
    const formikRef = useRef<FormikProps<any> | null>(null);

    const [selectedTravelers, setSelectedTravelers] = useState<SelectionState>({
        all: true,
        selected: [],
    });

    const { data: templates } = useCollection<MessageTemplate>("messageTemplates");

    const {
        mutateAsync: createMessage,
        isLoading,
        isSuccess,
    } = useMutation(async (message: any) => {
        try {
            await api.post(`/sellers/${seller.id}/messages`, message);

            const values = formikRef.current?.values;
            if (values !== undefined) {
                const { enableEmail, enableSms } = values;
                const isAllTravelersSelected = selectedTravelers.all;
                const text = getFlashMessageText(enableEmail, enableSms, isAllTravelersSelected);

                flash.success(text);
            }
        } catch {
            // Intentionally empty for now for axios interceptor to show message
        }
    });

    const enabledChannels = seller.preferences.communicationChannels?.enabledChannels ?? [];
    const isSmsAvailable = enabledChannels.includes(CommunicationChannelType.SMS) && !!isPhoneAvailable;
    const [emailGuides, setEmailGuides] = useState<boolean>(false);
    const [template, setTemplate] = useState<MessageTemplate | null>(null);

    const shouldShowTemplateSelector = isEmailAvailable || isSmsAvailable;

    const handleSubmit = async (values: FormState) => {
        const channels = getCreateMessagePayloadChannels(values);

        const sendMessageToSelectedTravelers = travelersList
            ? selectedTravelers.all
                ? (travelersList.map((traveler) => ({
                      id: traveler.id,
                      type: "traveler",
                  })) as MessageSendTo[])
                : (selectedTravelers.selected.map((traveler) => ({
                      id: traveler,
                      type: "traveler",
                  })) as MessageSendTo[])
            : [];

        const payload = {
            filters: filters,
            messageTemplate: { channels },
            to: travelersList ? sendMessageToSelectedTravelers : to ?? [{ type: "travelers" }],
            from: user.id,
        };

        if (emailGuides) {
            payload.to.push({ type: "guides" });
        }

        if (channels.length > 0) {
            await createMessage(payload);
        }

        handleSuccessfulSubmission(onClose);
        onContinue?.();
    };

    const initialValues: FormState = {
        email: { subject: "", body: "" },
        sms: { body: "" },
        enableEmail: isEmailAvailable,
        enableSms: isSmsAvailable,
    };

    const isNoSelectedTravelers = travelersList && selectedTravelers.selected.length === 0 && !selectedTravelers.all;

    const validationSchema = getValidationSchema({
        template,
    });
    return (
        <XolaFormik
            innerRef={formikRef}
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
        >
            {({ handleSubmit, values, setFieldValue }) => (
                <>
                    <Modal.Body>
                        <fieldset disabled={isLoading} className="min-w-0">
                            {children}

                            {travelersList && (
                                <div className="mt-10">
                                    <FormGroup>
                                        {travelersList && (
                                            <>
                                                <Label>Recipients</Label>

                                                <TravelersSelector
                                                    value={selectedTravelers}
                                                    travelersList={travelersList}
                                                    onChange={(selectionState: SelectionState) =>
                                                        setSelectedTravelers(selectionState)
                                                    }
                                                />
                                            </>
                                        )}
                                    </FormGroup>

                                    {getInfoText(selectedTravelers, travelersList) && (
                                        <Alert
                                            color={
                                                selectedTravelers.selected.length === 0 && !selectedTravelers.all
                                                    ? "danger"
                                                    : "warning"
                                            }
                                        >
                                            <div className="flex items-center space-x-2">
                                                <WarningDiamondIcon />
                                                <span>{getInfoText(selectedTravelers, travelersList)}</span>
                                            </div>
                                        </Alert>
                                    )}
                                </div>
                            )}

                            <div>
                                <FormGroup>
                                    {shouldShowEmailGuides && (
                                        <IsGranted permissions={Permission.RESOURCES_GUIDES_ALL_VIEW}>
                                            <div className="my-4">
                                                <ChannelSwitch
                                                    label="Email Guides"
                                                    isChecked={emailGuides}
                                                    onChange={setEmailGuides}
                                                />
                                            </div>
                                        </IsGranted>
                                    )}

                                    {shouldShowTemplateSelector && (
                                        <>
                                            <Label>Load Template</Label>

                                            <TemplateSelect
                                                className="flex-1"
                                                value={template}
                                                templates={templates}
                                                dataTestId="message-modal-template-select"
                                                onChange={(template) => {
                                                    setTemplate(template);

                                                    setFieldValue("email", {
                                                        subject: template?.email?.subject ?? "",
                                                        body: template?.email?.body ?? "",
                                                    });

                                                    setFieldValue("sms", {
                                                        body: template?.sms?.body ?? "",
                                                    });
                                                }}
                                            />
                                        </>
                                    )}
                                </FormGroup>
                            </div>

                            <MessageForm
                                template={template}
                                isEmailAvailable={isEmailAvailable}
                                isSmsAvailable={isSmsAvailable}
                            />
                        </fieldset>
                    </Modal.Body>

                    <Modal.Footer>
                        <fieldset className="flex justify-end space-x-4" disabled={isLoading}>
                            <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>

                            <SubmitButton
                                isLoading={isLoading}
                                isSuccess={isSuccess}
                                disabled={(!values.enableEmail && !values.enableSms) || isNoSelectedTravelers}
                                onClick={handleSubmit}
                            >
                                Send
                            </SubmitButton>
                        </fieldset>
                    </Modal.Footer>
                </>
            )}
        </XolaFormik>
    );
};

interface GetValidationSchemaParams {
    template: MessageTemplate | null;
}

const getInfoText = (selectionState: SelectionState, travelerList: TravelerMessage[]) => {
    const isOrganizer = selectionState.selected.length === 1 && travelerList[0].id === selectionState.selected[0];

    if (selectionState.all) {
        return "This message is sent to all the attendees in this reservation.";
    }

    if (isOrganizer) {
        return "This message is sent to the organizer of this reservation.";
    }

    if (selectionState.selected.length === 0) {
        return "At least one recipient must be selected";
    }

    return null;
};

export const getValidationSchema = ({ template }: GetValidationSchemaParams) => {
    return object({
        email: object().when("enableEmail", (enableEmail: boolean) => {
            const shouldValidateEmail = enableEmail && !(template && !template?.email);

            return object({
                subject: shouldValidateEmail ? requiredString() : string(),
                body: shouldValidateEmail ? requiredString() : string(),
            });
        }),

        sms: object().when("enableSms", (enableSms: boolean) => {
            const shouldValidateSms = enableSms ? !(template && !template?.sms?.body) : false;

            return object({
                body: shouldValidateSms ? requiredString().max(737, "must have less than 737 characters") : string(),
            });
        }),
    });
};

export const getCreateMessagePayloadChannels = ({ enableEmail, enableSms, email, sms }: FormState) => {
    const channels = [];

    if (enableEmail) {
        const { subject, body } = email;
        channels.push({ subject, body, type: CommunicationChannelType.EMAIL });
    }

    if (enableSms) {
        const { body } = sms;
        channels.push({ body, type: CommunicationChannelType.SMS });
    }

    return channels;
};

const getFlashMessageText = (enableEmail: boolean, enableSms: boolean, isAllTravelersSelected: boolean) => {
    if (enableEmail && enableSms) {
        return isAllTravelersSelected
            ? "Message will be sent to guests who have provided valid contact information"
            : "Message will be sent to selected guest(s).";
    } else if (enableEmail) {
        return isAllTravelersSelected
            ? "Email will be sent to guests who have provided a valid email address"
            : "Email will be sent to selected guest(s).";
    } else if (enableSms) {
        return isAllTravelersSelected
            ? "SMS will be sent to guests who have provided a valid phone number"
            : "SMS will be sent to selected guest(s).";
    }

    return "";
};
